import type { BlogPost } from './types';

export const post: BlogPost = {
  slug: 'why-your-favorite-team-is-your-worst-bet',
  title: 'Why your favorite team is your worst bet',
  excerpt:
    'You watch them the most, so you should know them best. Somehow the notes keep saying otherwise. A love story, with math.',
  author: 'Sloane',
  date: '2026-07-17',
  category: 'Bankroll',
  evergreen: true,
  tags: ['fandom', 'bias', 'bankroll', 'discipline'],
  body: `# Why your favorite team is your worst bet

The theory is airtight. You watch every game your team plays. You know the rotation, the injuries, which lineman is quietly washed, which reliever cannot be trusted with a two-run lead on the road. You have more information about this one team than about the other twenty-nine combined. Betting them should be your specialty.

Track it for a season and then we can talk. Mine is in the notes, and the notes are not kind.

The problem is not the information. The information is real. The problem is that every piece of it passes through the most compromised analyst in the building: a fan. You do not evaluate your team's schedule. You feel it. Their upcoming opponent is never simply good; it is a "statement opportunity." A two-game skid is never just variance; it is either doom or, worse, the exact reason they are "due." You are not reading the matchup. You are managing a relationship.

## The market charges for love

The market knows this, by the way, and it charges for it. Popular teams with big television footprints carry a standing tax. When half a state auto-bets the same side every week out of loyalty, the book does not need to be brave; it shades the number and lets devotion pay the [juice](/blog/the-juice-explained). If your team is nationally beloved, you are not just betting with your heart. You are betting with everyone else's, at a markup.

And then there is the double loss, which is the part nobody prices until they feel it. When your team loses a game you bet on, you take the fan loss and the bankroll loss in the same evening, and they compound. The night gets a specific texture that a Tuesday loss on a team you have no feelings about simply does not have. Bad emotional nights produce bad next-day bets. It is a chain, and it starts with loyalty.

Some options, in ascending order of discipline.

## Put the bias on a leash

**Track them separately.** One column in the notes: bets on your team, and how they did. For most people this experiment resolves itself in about two months, quietly and permanently.

**Bet them only against your bias.** If you cannot quit, allow yourself their games only when the bet disagrees with your heart. Wanting them to win and betting them to lose at least means one of you is thinking.

**Or retire the whole category.** Declare your team a no-bet zone and just be a fan. Watching a game where the only thing at stake is joy turns out to be the premium product. It cost everything to learn that it is free.

For the record, Frankie has bet our team every single week since we started this publication. When I brought her the spreadsheet, she said the data "lacked heart."

The data lacked nothing. It had her record in it.`,
};
